import React, { MouseEventHandler, useCallback } from 'react';

import { GADataType, I18nText } from '../../../types';

import { Translate } from '../../../helpers/Translate';

type PureButtonHelperOnClick = (e: React.MouseEvent<HTMLButtonElement>) => void;

interface PureButtonHelperDefaultProps {
  id?: string;
  blurOnMouseLeave?: boolean;
  className?: string;
  disabled?: boolean;
  onClick?: PureButtonHelperOnClick;
  onMouseEnter?: () => void;
  onPointerDown?: (e: React.PointerEvent<HTMLButtonElement>) => void;
  onTouchStart?: (e: React.TouchEvent<HTMLButtonElement>) => void;
  onTouchEnd?: (e: React.TouchEvent<HTMLButtonElement>) => void;
}

interface PureButtonHelperWithI18nProps {
  i18nTextClassName?: string;
  i18nText: I18nText;
  text?: never;
}

interface PureButtonHelperWithTextProps {
  i18nTextClassName?: never;
  i18nText?: never;
  text: string;
}

type PureButtonHelperProps = PureButtonHelperDefaultProps &
  GADataType &
  (PureButtonHelperWithI18nProps | PureButtonHelperWithTextProps);

function PureButtonHelper({
  blurOnMouseLeave,
  className,
  dataGa,
  disabled,
  i18nText,
  i18nTextClassName,
  id,
  onClick,
  onMouseEnter,
  onPointerDown,
  onTouchStart,
  onTouchEnd,
  text
}: PureButtonHelperProps) {
  const handleClick = useCallback<PureButtonHelperOnClick>(
    (e) => {
      e.preventDefault();
      onClick?.(e);
    },
    [onClick]
  );

  const handleMouseLeave = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => (event.target as HTMLButtonElement)?.blur(),
    []
  );

  return (
    <button
      className={className}
      data-ga={dataGa}
      disabled={disabled}
      id={id}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={blurOnMouseLeave ? handleMouseLeave : undefined}
      onPointerDown={onPointerDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      type="button"
    >
      {i18nText && i18nTextClassName ? (
        <span className={i18nTextClassName}>
          <Translate id={i18nText} />
        </span>
      ) : null}
      {i18nText && !i18nTextClassName ? <Translate id={i18nText} /> : null}
      {text}
    </button>
  );
}

export default PureButtonHelper;
